import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getInsight, nextInsight } from "../data/insights";
import { useSEO, useJsonLd } from "../lib/seo";
import { FadeUp } from "../components/motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n";

export default function InsightDetail() {
  const { slug } = useParams();
  const { t, lang, href } = useLang();
  const a = getInsight(slug);
  const next = nextInsight(slug);
  useSEO(a ? `${t(a.title)} — YUXEL` : t("Insights — YUXEL"), a ? t(a.excerpt) : "YUXEL insights", { type: "article" });
  useJsonLd(
    "article",
    a && {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: t(a.title),
      description: t(a.excerpt),
      ...(a.author?.name ? {author: { "@type": "Person", name: a.author.name }} : {}),
      publisher: { "@type": "Organization", name: "YUXEL" },
      articleSection: t(a.category),
      inLanguage: lang,
      mainEntityOfPage: window.location.origin + href(`/insights/${a.slug}`),
    }
  );

  if (!a) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 pt-20 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">404</p>
        <h1 className="page-display-title mt-4 text-white">{t("Article not found")}</h1>
        <Link
          to={href("/insights")}
          data-testid="insight-detail-notfound-link"
          className="mt-8 rounded-full bg-gold px-6 py-3 font-semibold text-ink"
        >
          {t("All insights")}
        </Link>
      </div>
    );
  }

  return (
    <div className="creative-insight-detail">
      <header className="relative overflow-hidden pb-14 pt-32 sm:pt-40">
        <div className="spotlight-top absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
          <Link
            to={href("/insights")}
            data-testid="insight-detail-back-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/60 transition-colors hover:text-gold"
          >
            <ArrowLeft className="h-4 w-4" /> {t("All insights")}
          </Link>
          <p className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-xs uppercase tracking-[0.25em] text-gold">
            <span>{t(a.category)}</span>
            <span className="text-white/40">{t(a.date)}</span>
            <span className="text-white/40">{t(a.readTime)}</span>
          </p>
          <h1 className="page-display-title mt-6 max-w-5xl text-white">
            {t(a.title)}
          </h1>
          <p className="mt-6 font-serif text-xl italic leading-relaxed text-white/70 sm:text-2xl">
            {t(a.excerpt)}
          </p>
          {a.author?.name && <div
            data-testid="insight-detail-byline"
            className="mt-8 flex items-center gap-4 border-t border-white/10 pt-6"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-gold/10 font-display text-sm font-semibold text-gold">
              Y
            </span>
            <div>
              <p className="text-sm font-semibold text-white">{a.author.name}</p>
              <p className="mt-0.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">
                {t(a.author.role)}
              </p>
            </div>
            <Link
              to={href(`/insights?category=${encodeURIComponent(a.category)}`)}
              data-testid="insight-detail-category-link"
              className="ml-auto rounded-full border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 transition-colors hover:border-gold hover:text-gold"
            >
              {t("More in")} {t(a.category)}
            </Link>
          </div>}
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-5 pb-20 sm:px-8" aria-label="Article body">
        {a.sections.map((s, i) => (
          <FadeUp key={s.h} delay={Math.min(i * 0.05, 0.15)}>
            <div className="pt-12 first:pt-0" data-testid={`insight-section-${i}`}>
              <h2 className="font-display text-xl font-semibold text-white sm:text-2xl">{t(s.h)}</h2>
              {s.p.map((para, j) => (
                <p
                  key={j}
                  className="mt-5 text-base leading-[1.85] text-white/75 sm:text-lg"
                >
                  {t(para)}
                </p>
              ))}
            </div>
          </FadeUp>
        ))}
      </article>

      <section className="mx-auto max-w-4xl px-5 pb-24 sm:px-8 sm:pb-32" aria-label="Next article">
        <FadeUp>
          <Link
            to={href(`/insights/${next.slug}`)}
            data-testid="insight-next-link"
            className="group flex items-center justify-between gap-6 border border-white/10 bg-panel p-7 transition-colors duration-500 hover:border-gold/40 sm:p-9"
          >
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">{t("Next insight")}</p>
              <h2 className="mt-3 font-display text-xl font-semibold text-white group-hover:text-champagne sm:text-2xl">
                {t(next.title)}
              </h2>
            </div>
            <ArrowRight className="h-6 w-6 shrink-0 text-white/40 transition-all duration-300 group-hover:translate-x-2 group-hover:text-gold" />
          </Link>
        </FadeUp>
      </section>
    </div>
  );
}
